import { useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { Heart, X, MapPin, Clock, BadgeCheck, MessageCircle } from 'lucide-react';
import type { DiscoveryCardProps, SwipeActionType } from '../types';

const SWIPE_THRESHOLD = 100;

export function DiscoveryCard({
  listing,
  zIndex,
  style,
  onSwipe,
  onTap,
}: DiscoveryCardProps): JSX.Element {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const opacity = useTransform(
    x,
    [-200, -100, 0, 100, 200],
    [0.5, 1, 1, 1, 0.5]
  );
  const parallaxX = useTransform(x, (v: number) => v * 0.1);

  const [swipeDirection, setSwipeDirection] = useState<'like' | 'pass' | null>(null);

  const handleDragEnd = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: { offset: { x: number }; velocity: { x: number } }
  ) => {
    const swipe = info.offset.x;
    const velocity = info.velocity.x;

    if (Math.abs(swipe) > SWIPE_THRESHOLD || Math.abs(velocity) > 500) {
      const direction = swipe > 0 ? 'right' : 'left';
      const action: SwipeActionType = direction === 'right' ? 'like' : 'pass';
      
      x.set(direction === 'right' ? 500 : -500);
      onSwipe(action);
    } else {
      x.set(0);
      setSwipeDirection(null);
    }
  };

  const handleDrag = (
    _event: MouseEvent | TouchEvent | PointerEvent,
    info: { offset: { x: number } }
  ) => {
    const swipe = info.offset.x;
    if (swipe > 50) {
      setSwipeDirection('like');
    } else if (swipe < -50) {
      setSwipeDirection('pass');
    } else {
      setSwipeDirection(null);
    }
  };

  const handleButtonSwipe = (action: SwipeActionType) => {
    const xOffset = action === 'like' || action === 'super' ? 500 : -500;
    x.set(xOffset);
    onSwipe(action);
  };

  return (
    <motion.div
      className="absolute inset-0 touch-none cursor-grab active:cursor-grabbing"
      style={{ zIndex, ...style, x, rotate, opacity }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      onClick={onTap}
    >
      {/* Card */}
      <div className="relative w-full h-full bg-white rounded-3xl overflow-hidden shadow-2xl">
        {/* Image */}
        <div className="relative h-3/5 overflow-hidden">
          <motion.img
            src={listing.images[0]}
            alt={listing.title}
            className="w-full h-full object-cover"
            style={{ x: parallaxX }}
          />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Match score badge */}
          {listing.matchScore > 80 && (
            <div className="absolute top-4 left-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
              <span>✨</span> {listing.matchScore}% Match
            </div>
          )}

          {/* Swipe overlay indicators */}
          {swipeDirection && (
            <div
              className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ${
                swipeDirection === 'like' ? 'bg-success/20' : 'bg-danger/20'
              }`}
              style={{ opacity: Math.abs(x.get()) / 200 }}
            >
              <div
                className={`border-4 rounded-2xl p-4 transform rotate-12 ${
                  swipeDirection === 'like'
                    ? 'border-success text-success'
                    : 'border-danger text-danger'
                }`}
              >
                {swipeDirection === 'like' ? (
                  <Heart className="w-16 h-16 fill-current" />
                ) : (
                  <X className="w-16 h-16" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-5 bg-white">
          {/* Title & Price */}
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xl font-bold text-dark-900 line-clamp-2 flex-1 mr-3">
              {listing.title}
            </h3>
            <span className="text-xl font-bold text-primary-600">
              PKR {listing.price.toLocaleString()}
            </span>
          </div>

          {/* Seller info */}
          <div className="flex items-center gap-2 mb-3">
            <div className="relative">
              <img
                src={listing.seller.avatar}
                alt={listing.seller.name}
                className="w-8 h-8 rounded-full object-cover"
              />
              {listing.seller.isOnline && (
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-success rounded-full border-2 border-white" />
              )}
            </div>
            <span className="text-sm font-medium text-dark-700">{listing.seller.name}</span>
            {listing.seller.trustScore > 70 && (
              <BadgeCheck className="w-4 h-4 text-primary-500" />
            )}
          </div>

          {/* Meta */}
          <div className="flex items-center gap-4 text-sm text-dark-500 mb-4">
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {listing.location}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {new Date(listing.postedAt).toLocaleDateString()}
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-4 pt-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleButtonSwipe('pass');
              }}
              className="w-14 h-14 rounded-full bg-dark-100 text-dark-500 flex items-center justify-center shadow-lg"
            >
              <X className="w-6 h-6" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onSwipe('save');
              }}
              className="w-12 h-12 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center shadow-lg"
            >
              <MessageCircle className="w-5 h-5" />
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleButtonSwipe('like');
              }}
              className="w-14 h-14 rounded-full bg-gradient-to-br from-success to-emerald-600 text-white flex items-center justify-center shadow-lg shadow-success/30"
            >
              <Heart className="w-6 h-6" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
